/**
 * What a group will hold, and how large. Pure — the bytes arrive as a parameter, nothing
 * here reads a stream or a clock.
 *
 * The declared content type is a *claim*; the leading bytes are the *evidence*. Nothing is
 * stored on the claim alone — `docs/reviews/2026-09-11-objectives-security.md` is what it
 * cost to learn that once, and the `Content-Length`-after-buffering finding in it is why
 * `declaredSize` is checked before anything looks at a byte.
 *
 * R15 is enforced above this function, in words at the upload point: materials are
 * teacher-authored instructional materials, and student work and student records must not
 * be uploaded. There is no detector here and there should not be one — the schema having
 * no column for a student is the structural half, and the sentence is the human half.
 */

/** R11's ceiling, and the same number as `CHECK(size_bytes BETWEEN 0 AND 10485760)`. */
export const MAX_MATERIAL_BYTES = 10 * 1024 * 1024;

/** Enough leading bytes for every signature below. The Worker may pass more. */
export const SIGNATURE_BYTES = 1024;

export type MaterialFormat = 'pdf' | 'docx' | 'pptx' | 'png' | 'jpeg' | 'text';

/**
 * Why an upload is turned away. The last two come from `inspectOffice`
 * (`core/office-inspection.ts`) and are the only ones a teacher can fix inside the file
 * itself; a malformed zip or a zip bomb is `content_mismatch`, like any other file that is
 * not what it says it is.
 */
export type UploadRefusal =
  | 'unsupported_type' | 'too_large' | 'empty' | 'content_mismatch'
  | 'office_macros' | 'office_linked_content';

export type UploadDecision =
  | { ok: true; format: MaterialFormat; contentType: string }
  | { ok: false; reason: UploadRefusal };

/**
 * The type stored on the R2 object and in the row, regardless of what the browser said.
 * A download is served as `application/octet-stream` either way (R13) — this is the
 * canonical label, not a promise to serve it.
 */
export const CANONICAL_CONTENT_TYPE: Readonly<Record<MaterialFormat, string>> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  png: 'image/png',
  jpeg: 'image/jpeg',
  text: 'text/plain',
};

const BY_CONTENT_TYPE: Readonly<Record<string, MaterialFormat>> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'text/plain': 'text',
};

/**
 * Refused by name, not merely absent from the list above.
 *
 * SVG and HTML are documents that execute. The SPA, the uploaded bytes and the invitation
 * token in `sessionStorage` share one origin, so a stored `image/svg+xml` that a browser
 * ever renders is a stored XSS against the Clerk session. R13's pinned
 * `application/octet-stream`, `nosniff` and sandbox CSP are the second line; this is the
 * first, and it is listed explicitly so that widening the allow list cannot readmit them
 * by accident.
 */
export const REFUSED_CONTENT_TYPES: readonly string[] = [
  'image/svg+xml',
  'text/html',
  'application/xhtml+xml',
  'text/xml',
  'application/xml',
];

/** The declared type, stripped of parameters and case. */
export function formatOfContentType(contentType: string): MaterialFormat | null {
  const mime = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (REFUSED_CONTENT_TYPES.includes(mime)) return null;
  return BY_CONTENT_TYPE[mime] ?? null;
}

/** How a previewable material is put on screen. Nothing else has a viewer (U11). */
export type PreviewKind = 'pdf' | 'image';

/**
 * The only stored types that may ever be served inline, and the only types the browser may
 * ever build a blob URL with.
 *
 * Three, and they are three because each of them is **inert**: a PNG and a JPEG cannot
 * execute at all, and a PDF's own scripting is what the response's `default-src 'none';
 * sandbox` and the viewer's `sandbox` iframe attribute take away. Every other stored type —
 * `text/plain`, both OOXML formats, and anything a row acquired by some path that is not the
 * upload handler — is served as a download and nothing else.
 *
 * `image/svg+xml` and `text/html` are absent and must stay absent. They are documents that
 * execute, `REFUSED_CONTENT_TYPES` already turns them away at upload, and this list is the
 * second refusal: the SPA, the material bytes and the invitation token share one origin, so
 * an SVG rendered inline is a stored XSS against the Clerk session. A "preview" that widens
 * this map by one entry is the whole of that attack.
 */
const PREVIEWABLE: Readonly<Record<string, PreviewKind>> = {
  'application/pdf': 'pdf',
  'image/png': 'image',
  'image/jpeg': 'image',
};

/** The allowlisted type a preview is served as, or null. Never the caller's spelling. */
export function previewContentType(contentType: string): string | null {
  const mime = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (REFUSED_CONTENT_TYPES.includes(mime)) return null;
  return mime in PREVIEWABLE ? mime : null;
}

/** How to render it, or null when this material has no viewer and only a download. */
export function previewKindOfContentType(contentType: string): PreviewKind | null {
  const mime = previewContentType(contentType);
  return mime === null ? null : PREVIEWABLE[mime] ?? null;
}

/** True when `bytes` holds `signature` at `offset`. A too-short input is false. */
export const startsWith = (bytes: Uint8Array, signature: readonly number[], offset = 0): boolean =>
  signature.every((expected, i) => bytes[offset + i] === expected);

const ascii = (text: string): number[] => [...text].map((c) => c.charCodeAt(0));

/**
 * The PDF spec lets junk precede the header and some generators use that, so the marker is
 * searched for in the leading window rather than demanded at offset zero.
 */
function hasPdfHeader(bytes: Uint8Array): boolean {
  const marker = ascii('%PDF-');
  const end = Math.min(bytes.length - marker.length, SIGNATURE_BYTES);
  for (let i = 0; i <= end; i += 1) if (startsWith(bytes, marker, i)) return true;
  return false;
}

/**
 * Well-formed UTF-8 per RFC 3629, by hand — `core/` has no TextDecoder and should not:
 * this is arithmetic on bytes, not I/O. A sequence cut off by the end of the window is
 * allowed, because the window is a prefix of the file and not the file.
 */
export function isTextPrefix(bytes: Uint8Array): boolean {
  if (bytes.includes(0)) return false;
  let i = 0;
  while (i < bytes.length) {
    const b0 = bytes[i]!;
    if (b0 < 0x80) { i += 1; continue; }

    let needed: number;
    let min: number;
    let codePoint: number;
    if (b0 >= 0xc2 && b0 <= 0xdf) { needed = 1; min = 0x80; codePoint = b0 & 0x1f; }
    else if (b0 >= 0xe0 && b0 <= 0xef) { needed = 2; min = 0x800; codePoint = b0 & 0x0f; }
    else if (b0 >= 0xf0 && b0 <= 0xf4) { needed = 3; min = 0x10000; codePoint = b0 & 0x07; }
    else return false;

    // Truncated by the window, not malformed. Stop rather than refuse.
    if (i + needed >= bytes.length) return true;

    for (let k = 1; k <= needed; k += 1) {
      const b = bytes[i + k]!;
      if ((b & 0xc0) !== 0x80) return false;
      codePoint = (codePoint << 6) | (b & 0x3f);
    }
    if (codePoint < min || codePoint > 0x10ffff) return false;
    if (codePoint >= 0xd800 && codePoint <= 0xdfff) return false;
    i += needed + 1;
  }
  return true;
}

const SIGNATURE_CHECK: Readonly<Record<MaterialFormat, (bytes: Uint8Array) => boolean>> = {
  pdf: hasPdfHeader,
  // Both OOXML formats are zips, and the zip local-file header is all the leading bytes
  // can prove. Telling a .docx from a .pptx needs the central directory, which is at the
  // *end* of the file — out of reach of a signature check, and not worth buffering 10 MB
  // to reach. The declaration decides which of the two it is; the bytes decide it is a zip.
  docx: (bytes) => startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]),
  pptx: (bytes) => startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]),
  png: (bytes) => startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  jpeg: (bytes) => startsWith(bytes, [0xff, 0xd8, 0xff]),
  text: isTextPrefix,
};

export interface UploadInput {
  /** What the browser claims the file is. */
  contentType: string;
  /** What the browser claims it weighs — checked first, and again against the real body. */
  declaredSize: number;
  /** The leading bytes of the body, up to `SIGNATURE_BYTES` of them. */
  magicBytes: Uint8Array;
}

export function decideUpload({ contentType, declaredSize, magicBytes }: UploadInput): UploadDecision {
  // Size first, before anything looks at the type or the bytes. A 100 MB file is turned
  // away on its size rather than walked to discover what it is.
  if (!Number.isFinite(declaredSize) || declaredSize <= 0) return { ok: false, reason: 'empty' };
  if (declaredSize > MAX_MATERIAL_BYTES) return { ok: false, reason: 'too_large' };

  const format = formatOfContentType(contentType);
  if (!format) return { ok: false, reason: 'unsupported_type' };

  if (!SIGNATURE_CHECK[format](magicBytes)) return { ok: false, reason: 'content_mismatch' };

  return { ok: true, format, contentType: CANONICAL_CONTENT_TYPE[format] };
}

/**
 * The only size an event ever carries. A byte count is a fingerprint of a specific file;
 * a bucket answers "are teachers sharing slide decks or one-pagers" without being one.
 */
export type SizeBucket = 'under_100kb' | 'under_1mb' | 'under_5mb' | 'under_10mb';

export function sizeBucket(bytes: number): SizeBucket {
  if (bytes < 100 * 1024) return 'under_100kb';
  if (bytes < 1024 * 1024) return 'under_1mb';
  if (bytes < 5 * 1024 * 1024) return 'under_5mb';
  return 'under_10mb';
}

/**
 * The cross-group search's query string (planner collab materials KTD3), read without I/O.
 *
 * `q` is a title fragment, trimmed, at most as long as a title may be; empty matches every
 * title. The Worker matches it with `instr(lower(title), lower(?))` rather than `LIKE`, so `%`
 * and `_` are the characters themselves. `limit` is a positive whole number, held at 50; absent
 * or empty is 50.
 */
export const MAX_SEARCH_RESULTS = 50;
export const MAX_SEARCH_QUERY = 200;

export type MaterialSearch =
  | { ok: true; q: string; limit: number }
  | { ok: false; reason: 'invalid_query' | 'invalid_limit' };

export function parseMaterialSearch(rawQuery: string | null, rawLimit: string | null): MaterialSearch {
  const q = (rawQuery ?? '').trim();
  if (q.length > MAX_SEARCH_QUERY) return { ok: false, reason: 'invalid_query' };
  if (rawLimit === null || rawLimit === '') return { ok: true, q, limit: MAX_SEARCH_RESULTS };
  if (!/^[0-9]+$/.test(rawLimit)) return { ok: false, reason: 'invalid_limit' };
  const limit = Number(rawLimit);
  if (limit < 1) return { ok: false, reason: 'invalid_limit' };
  return { ok: true, q, limit: Math.min(limit, MAX_SEARCH_RESULTS) };
}
